// Email delivery settings (PB-13-04; email stream PD-580 – PD-589; owner decision B-02). The SMTP
// password is write-only: requests may set, clear or (installation only) reference it; responses
// only say whether one is in place and when it last changed. Messages are i18n keys.
import { z } from 'zod';

export const MAIL_PROVIDER_VALUES = ['development_mailbox', 'smtp', 'none'] as const;
export type MailProviderDto = (typeof MAIL_PROVIDER_VALUES)[number];
export const SMTP_SECURITY_VALUES = ['tls', 'starttls', 'none'] as const;
export type SmtpSecurityDto = (typeof SMTP_SECURITY_VALUES)[number];
/** accepted = the server took it (never "delivered"); refused = 5xx; deferred = 4xx; not_connected = nothing sent; unknown = timeout after handing over. */
export type MailTestOutcomeDto = 'accepted' | 'refused' | 'deferred' | 'not_connected' | 'unknown' | 'unavailable';

const noControl = (s: string) => ![...s].some((c) => c.charCodeAt(0) < 0x20 || c.charCodeAt(0) === 0x7f);
const blankToNull = (v: string) => (v === '' ? null : v);
const Version = z.number().int().min(1);
const Address = z.email('v.email').max(254, 'v.tooLong');
const OptionalAddress = z.string().trim().max(254, 'v.tooLong').transform(blankToNull).pipe(Address.nullable()).nullable();
const DisplayName = z.string().trim().max(120, 'v.tooLong').refine(noControl, 'v.invalid').transform(blankToNull).nullable();
// Blank means "not set": blanks become null before the format check.
const Host = z
  .string()
  .trim()
  .max(253, 'v.tooLong')
  .transform(blankToNull)
  .pipe(z.string().regex(/^(?:[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)*$|^\[?[0-9A-Fa-f:.]+\]?$/, 'eml.v.host').nullable())
  .nullable();
const Domain = z
  .string()
  .trim()
  .toLowerCase()
  .transform(blankToNull)
  .pipe(z.string().regex(/^([a-z0-9]([a-z0-9-]{0,61}[a-z0-9])?\.)+[a-z]{2,63}$/, 'eml.v.domain').nullable())
  .nullable();
const Username = z.string().trim().max(320, 'v.tooLong').refine(noControl, 'v.invalid').transform(blankToNull).nullable();
/** Passwords may contain spaces, but never line breaks or other control characters (SMTP AUTH). */
const Password = z.string().min(1, 'v.required').max(1024, 'v.tooLong').refine(noControl, 'v.invalid');
const Reason = z.string().trim().min(3, 'v.reasonShort').max(500, 'v.tooLong');

export const SMTP_PASSWORD_ENV_PATTERN = /^PASSAGE_SMTP_[A-Z0-9_]{1,60}$/;

const PasswordChange = z.discriminatedUnion('kind', [
  z.object({ kind: z.literal('keep') }),
  z.object({ kind: z.literal('set'), value: Password }),
  z.object({ kind: z.literal('clear') }),
  // PD-583: deployments that prefer it name a PASSAGE_SMTP_* variable (or its _FILE) instead.
  z.object({ kind: z.literal('environment'), name: z.string().regex(SMTP_PASSWORD_ENV_PATTERN, 'eml.v.envName') }),
]);

const SenderFields = {
  fromAddress: OptionalAddress,
  fromNameEn: DisplayName,
  fromNameAr: DisplayName,
  replyTo: OptionalAddress,
  smtpHost: Host,
  smtpPort: z.number().int('v.invalid').min(1, 'v.range').max(65535, 'v.range').nullable(),
  smtpSecurity: z.enum(SMTP_SECURITY_VALUES).nullable(),
  smtpUsername: Username,
};

export const UpdateInstallationEmailSettingsRequest = z.object({
  expectedVersion: Version,
  provider: z.enum(MAIL_PROVIDER_VALUES),
  ...SenderFields,
  password: PasswordChange.default({ kind: 'keep' }),
});
export type UpdateInstallationEmailSettingsRequest = z.infer<typeof UpdateInstallationEmailSettingsRequest>;

export const UpdateOrganisationEmailSettingsRequest = z.object({
  expectedVersion: Version,
  enabled: z.boolean(),
  sendingDomain: Domain,
  ...SenderFields,
  password: PasswordChange.default({ kind: 'keep' }),
});
export type UpdateOrganisationEmailSettingsRequest = z.infer<typeof UpdateOrganisationEmailSettingsRequest>;

export const SendTestEmailRequest = z.object({ to: Address });
export const SetOrganisationEmailAllowedRequest = z.object({ allowed: z.boolean(), reason: Reason });

export interface EmailSenderDto {
  readonly fromAddress: string | null;
  readonly fromNameEn: string | null;
  readonly fromNameAr: string | null;
  readonly replyTo: string | null;
}
export interface SmtpConnectionDto {
  readonly host: string | null;
  readonly port: number | null;
  readonly security: SmtpSecurityDto | null;
  readonly username: string | null;
}
/** Never the password itself. */
export interface PasswordStateDto {
  readonly source: 'none' | 'stored' | 'environment';
  readonly environmentName: string | null;
  readonly version: number;
  readonly updatedAt: string | null;
}
export interface EmailTestStateDto {
  readonly at: string | null;
  readonly outcome: MailTestOutcomeDto | null;
}

export interface InstallationEmailSettingsResponse {
  readonly provider: MailProviderDto;
  readonly sender: EmailSenderDto;
  readonly smtp: SmtpConnectionDto;
  readonly password: PasswordStateDto;
  readonly version: number;
  readonly updatedAt: string;
  /** Why these settings cannot send now (empty = complete); i18n keys under `eml.problem.*`. */
  readonly problems: readonly string[];
  readonly lastTest: EmailTestStateDto & { readonly version: number | null };
  /** The development mailbox's fallback sender (MAIL_FROM) is not shown; only whether stored passwords can be sealed. */
  readonly secretKeysConfigured: boolean;
}

export interface OrganisationEmailOverrideDto {
  readonly enabled: boolean;
  readonly sendingDomain: string | null;
  readonly sender: EmailSenderDto;
  readonly smtp: SmtpConnectionDto;
  readonly password: PasswordStateDto;
  readonly version: number;
  readonly updatedAt: string;
  /** A test email for exactly this version was accepted by the server. */
  readonly verifiedCurrent: boolean;
  readonly problems: readonly string[];
  readonly lastTest: EmailTestStateDto;
}

export interface OrganisationEmailSettingsResponse {
  /** PD-581: a system administrator allowed this organisation to send as itself. */
  readonly allowed: boolean;
  /** Which sender the organisation's messages use now. */
  readonly inEffect: 'organisation' | 'installation';
  readonly installation: { readonly provider: MailProviderDto; readonly sender: EmailSenderDto };
  readonly override: OrganisationEmailOverrideDto | null;
}

export interface EmailTestResult {
  readonly outcome: MailTestOutcomeDto;
  /** SMTP reply code when the server answered. */
  readonly code: number | null;
  /** The server's reply text (password removed) or a problem code. */
  readonly detail: string | null;
  readonly settingsVersion: number;
}

export interface PlatformOrganisationEmailSummary {
  readonly allowed: boolean;
  readonly inEffect: 'organisation' | 'installation';
  readonly override: null | {
    readonly enabled: boolean;
    readonly sendingDomain: string | null;
    readonly fromAddress: string | null;
    readonly smtpHost: string | null;
    readonly verifiedCurrent: boolean;
    readonly lastTest: EmailTestStateDto;
    readonly version: number;
  };
}
